import { useState } from "react";
import { Button } from "../../../../components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../../components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../../components/ui/tooltip";
import { Trash2 } from "lucide-react";
import { PrescriptionType } from "../../../../utils/global-types";
import { deletePrescriptionAppointment } from "../../../../services/prescriptionAppointmentService";
import { toast } from "react-toastify";

export function DeleteDialog({
  row,
  getPrescriptionData,
}: {
  row: PrescriptionType;
  getPrescriptionData: any;
}) {
  const [open, setOpen] = useState(false);

  const closeAndReset = () => {
    setOpen(false);
    // Safety net: ensure Radix never leaves the page locked with
    // `pointer-events: none` on <body> after the modal closes.
    setTimeout(() => {
      document.body.style.pointerEvents = "";
    }, 0);
  };

  const handleDelete = () => {
    deletePrescriptionAppointment(row.id)
      .then(() => {
        toast.warning("Prescription Deleted Successfully");
        // Close the confirm first, then refresh on the next tick so the dialog
        // finishes closing before this row unmounts (avoids the body lock).
        closeAndReset();
        setTimeout(() => getPrescriptionData(row.appointmentId), 0);
      })
      .catch(() => {
        toast.error("Failed to delete prescription");
      });
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => (next ? setOpen(true) : closeAndReset())}
    >
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground hover:text-destructive"
              onClick={() => setOpen(true)}
            >
              <Trash2 className="h-4 w-4" />
              <span className="sr-only">Delete prescription</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>Delete prescription</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this prescription?</AlertDialogTitle>
          <AlertDialogDescription>
            This permanently removes the prescription from this appointment. This
            action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-1 rounded-md border bg-muted/40 p-3 text-sm">
          <div>
            <span className="text-muted-foreground">Brand Name: </span>
            {row.brandDrugName}
          </div>
          <div>
            <span className="text-muted-foreground">Generic Name: </span>
            {row.genericDrugName}
          </div>
          <div>
            <span className="text-muted-foreground">Formulation: </span>
            {row.brandDrugFormulationType}
          </div>
          <div>
            <span className="text-muted-foreground">Frequency: </span>
            {row.prescriptionFrequencyDescription}
          </div>
          <div>
            <span className="text-muted-foreground">Frequency Translation: </span>
            {row.prescriptionFrequencyTranslation}
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
